import React, { useState, useEffect } from 'react';
import { Card, Container, Row, Col, Table, Button, Badge, Form, Spinner, Alert, Modal } from 'react-bootstrap';
import apiService from '../services/api';

const EnterpriseAccessRightsManagement = () => {
    const [error, setError] = useState('');
    const [permissions, setPermissions] = useState([]);
    const [loadingPermissions, setLoadingPermissions] = useState(true);
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [searchCode, setSearchCode] = useState('');
    const [searchDescription, setSearchDescription] = useState('');
    const [searchCategory, setSearchCategory] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editingPermission, setEditingPermission] = useState(null);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({
        code: '',
        description: '',
        is_module_level: false,
        category: 'section'
    });

    // Connected Roles modal state
    const [showConnectedRolesModal, setShowConnectedRolesModal] = useState(false);
    const [connectedRolesPermission, setConnectedRolesPermission] = useState(null);
    const [connectedRoles, setConnectedRoles] = useState([]);
    const [loadingConnectedRoles, setLoadingConnectedRoles] = useState(false);

    useEffect(() => {
        loadPermissions();
    }, []);

    const loadPermissions = async () => {
        setLoadingPermissions(true);
        setError('');

        try {
            const response = await apiService.getAllEnterpriseAccessRights();
            if (response.success) {
                setPermissions(response.data || []);
            } else {
                setError(response.error || 'Failed to load permissions');
                setPermissions([]);
            }
        } catch (error) {
            console.error('Error loading permissions:', error);
            setError('Failed to load permissions');
            setPermissions([]);
        } finally {
            setLoadingPermissions(false);
        }
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const getFilteredAndSortedPermissions = () => {
        let filtered = permissions.filter(permission => {
            const codeMatch = !searchCode ||
                (permission.code && permission.code.toLowerCase().includes(searchCode.toLowerCase()));
            const descriptionMatch = !searchDescription ||
                (permission.description && permission.description.toLowerCase().includes(searchDescription.toLowerCase()));
            const categoryMatch = !searchCategory ||
                ((permission.category || 'section') === searchCategory);
            return codeMatch && descriptionMatch && categoryMatch;
        });

        if (!sortConfig.key) return filtered;

        const sorted = [...filtered].sort((a, b) => {
            let aVal = a[sortConfig.key];
            let bVal = b[sortConfig.key];

            if (aVal == null) aVal = '';
            if (bVal == null) bVal = '';

            if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }

            if (aVal < bVal) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aVal > bVal) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });

        return sorted;
    };

    const getSortIcon = (columnKey) => {
        if (sortConfig.key !== columnKey) {
            return <i className="bi bi-arrow-down-up ms-1 text-muted" style={{ fontSize: '0.8rem' }}></i>;
        }
        return sortConfig.direction === 'asc' ?
            <i className="bi bi-arrow-up ms-1"></i> :
            <i className="bi bi-arrow-down ms-1"></i>;
    };

    const handleShowModal = (permission = null) => {
        if (permission) {
            setEditingPermission(permission);
            setFormData({
                code: permission.code || '',
                description: permission.description || '',
                is_module_level: permission.is_module_level || false,
                category: permission.category || 'section'
            });
        } else {
            setEditingPermission(null);
            setFormData({
                code: '',
                description: '',
                is_module_level: false,
                category: 'section'
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setEditingPermission(null);
        setFormData({
            code: '',
            description: '',
            is_module_level: false,
            category: 'section'
        });
        setError('');
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleViewConnectedRoles = async (permission) => {
        setConnectedRolesPermission(permission);
        setConnectedRoles([]);
        setLoadingConnectedRoles(true);
        setShowConnectedRolesModal(true);

        try {
            const response = await apiService.getRolesByEnterpriseAccessRight(permission.id);
            if (response.success) {
                setConnectedRoles(response.data || []);
            } else {
                setError(response.error || 'Failed to load connected roles');
            }
        } catch (err) {
            console.error('Error loading connected roles:', err);
            setError('Failed to load connected roles');
        } finally {
            setLoadingConnectedRoles(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();

        if (!formData.code.trim()) {
            setError('Permission code is required');
            return;
        }
        if (!formData.description.trim()) {
            setError('Description is required');
            return;
        }

        setSaving(true);
        setError('');

        try {
            const permissionData = {
                code: formData.code.trim().toUpperCase(),
                description: formData.description.trim(),
                is_module_level: formData.is_module_level,
                category: formData.category
            };

            let response;
            if (editingPermission) {
                response = await apiService.updateEnterpriseAccessRight(editingPermission.id, permissionData);
            } else {
                response = await apiService.createEnterpriseAccessRight(permissionData);
            }

            if (response.success) {
                handleCloseModal();
                await loadPermissions();
            } else {
                setError(response.error || 'Failed to save access right');
            }
        } catch (error) {
            console.error('Error saving access right:', error);
            setError('Failed to save access right');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4">
                <Col>
                    <h2>
                        <i className="bi bi-shield-lock me-2"></i>
                        Enterprise Access Rights Management
                    </h2>
                    <p className="text-muted">Manage permissions and access controls for the enterprise</p>
                </Col>
                <Col xs="auto" className="d-flex align-items-center">
                    <Button
                        variant="primary"
                        onClick={() => handleShowModal()}
                    >
                        <i className="bi bi-plus-circle me-2"></i>
                        Add New Access Right
                    </Button>
                </Col>
            </Row>

            {error && (
                <Row className="mb-4">
                    <Col>
                        <Alert variant="danger" dismissible onClose={() => setError('')}>
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col lg={12}>
                    <Card className="shadow-sm">
                        <Card.Header>
                            <div className="d-flex justify-content-between align-items-center">
                                <h5 className="mb-0">
                                    <i className="bi bi-list-check me-2"></i>
                                    Enterprise Permissions
                                </h5>
                                {!loadingPermissions && permissions.length > 0 && (
                                    <Badge bg="info">{getFilteredAndSortedPermissions().length} / {permissions.length} permission{permissions.length !== 1 ? 's' : ''}</Badge>
                                )}
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {loadingPermissions ? (
                                <div className="text-center py-5">
                                    <Spinner animation="border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </Spinner>
                                    <p className="mt-3 text-muted">Loading permissions...</p>
                                </div>
                            ) : permissions.length === 0 ? (
                                <div className="text-center py-5 text-muted">
                                    <i className="bi bi-shield-x display-1"></i>
                                    <div className="mt-2">No permissions found</div>
                                </div>
                            ) : (
                                <>
                                    {/* Search Filters */}
                                    <Row className="mb-3">
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">
                                                    <i className="bi bi-search me-1"></i>
                                                    Search by Code
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter permission code..."
                                                    value={searchCode}
                                                    onChange={(e) => setSearchCode(e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">
                                                    <i className="bi bi-search me-1"></i>
                                                    Search by Description
                                                </Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    placeholder="Enter description..."
                                                    value={searchDescription}
                                                    onChange={(e) => setSearchDescription(e.target.value)}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={4}>
                                            <Form.Group>
                                                <Form.Label className="small fw-bold">
                                                    <i className="bi bi-funnel me-1"></i>
                                                    Filter by Category
                                                </Form.Label>
                                                <Form.Select
                                                    value={searchCategory}
                                                    onChange={(e) => setSearchCategory(e.target.value)}
                                                >
                                                    <option value="">All categories</option>
                                                    <option value="module">Module</option>
                                                    <option value="section">Section</option>
                                                    <option value="task">Task</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    {(searchCode || searchDescription || searchCategory) && (
                                        <div className="mb-3">
                                            <Button
                                                variant="outline-secondary"
                                                size="sm"
                                                onClick={() => {
                                                    setSearchCode('');
                                                    setSearchDescription('');
                                                    setSearchCategory('');
                                                }}
                                            >
                                                <i className="bi bi-x-circle me-1"></i>
                                                Clear Filters
                                            </Button>
                                        </div>
                                    )}
                                    {getFilteredAndSortedPermissions().length === 0 ? (
                                        <div className="text-center py-5 text-muted">
                                            <i className="bi bi-search display-4"></i>
                                            <div className="mt-2">No permissions match your search criteria</div>
                                            <Button
                                                variant="link"
                                                onClick={() => {
                                                    setSearchCode('');
                                                    setSearchDescription('');
                                                    setSearchCategory('');
                                                }}
                                            >
                                                Clear filters
                                            </Button>
                                        </div>
                                    ) : (
                                        <Table striped bordered hover responsive>
                                            <thead className="table-dark">
                                                <tr>
                                                    <th onClick={() => handleSort('code')} style={{ cursor: 'pointer' }}>
                                                        Permission Code {getSortIcon('code')}
                                                    </th>
                                                    <th onClick={() => handleSort('description')} style={{ cursor: 'pointer' }}>
                                                        Description {getSortIcon('description')}
                                                    </th>
                                                    <th onClick={() => handleSort('category')} style={{ cursor: 'pointer' }}>
                                                        Category {getSortIcon('category')}
                                                    </th>
                                                    <th onClick={() => handleSort('is_module_level')} style={{ cursor: 'pointer' }}>
                                                        Module Level {getSortIcon('is_module_level')}
                                                    </th>
                                                    <th onClick={() => handleSort('last_updated_at')} style={{ cursor: 'pointer' }}>
                                                        Last Updated {getSortIcon('last_updated_at')}
                                                    </th>
                                                    <th onClick={() => handleSort('created_at')} style={{ cursor: 'pointer' }}>
                                                        Created {getSortIcon('created_at')}
                                                    </th>
                                                    <th>Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getFilteredAndSortedPermissions().map((permission) => (
                                                    <tr key={permission.id}>
                                                        <td>
                                                            <code className="text-primary">{permission.code || '-'}</code>
                                                        </td>
                                                        <td>
                                                            <small className="text-muted">{permission.description || '-'}</small>
                                                        </td>
                                                        <td>
                                                            {(() => {
                                                                const category = permission.category || 'section';
                                                                const variant = category === 'module' ? 'primary' : category === 'task' ? 'info' : 'secondary';
                                                                return <Badge bg={variant}>{category.charAt(0).toUpperCase() + category.slice(1)}</Badge>;
                                                            })()}
                                                        </td>
                                                        <td>
                                                            {permission.is_module_level ? (
                                                                <Badge bg="primary">Yes</Badge>
                                                            ) : (
                                                                <Badge bg="secondary">No</Badge>
                                                            )}
                                                        </td>
                                                        <td>
                                                            <small>{apiService.formatDate(permission.last_updated_at)}</small>
                                                        </td>
                                                        <td>
                                                            <small>{apiService.formatDate(permission.created_at)}</small>
                                                        </td>
                                                        <td>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                className="me-2"
                                                                title="Edit"
                                                                onClick={() => handleShowModal(permission)}
                                                            >
                                                                <i className="bi bi-pencil"></i>
                                                            </Button>
                                                            <Button
                                                                variant="outline-info"
                                                                size="sm"
                                                                title="View Connected Roles"
                                                                onClick={() => handleViewConnectedRoles(permission)}
                                                            >
                                                                <i className="bi bi-link-45deg"></i>
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    )}
                                </>
                            )}
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Add/Edit Modal */}
            <Modal show={showModal} onHide={handleCloseModal} size="md">
                <Modal.Header closeButton>
                    <Modal.Title>
                        {editingPermission ? 'Edit Access Right' : 'Add Access Right'}
                    </Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSave}>
                    <Modal.Body>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError('')}>
                                <i className="bi bi-exclamation-triangle me-2"></i>
                                {error}
                            </Alert>
                        )}

                        <Form.Group className="mb-3">
                            <Form.Label>Permission Code *</Form.Label>
                            <Form.Control
                                type="text"
                                name="code"
                                value={formData.code}
                                onChange={handleInputChange}
                                placeholder="Enter permission code (e.g., READ_USERS, WRITE_DATA)"
                                required
                                maxLength={100}
                                style={{ textTransform: 'uppercase' }}
                                disabled={editingPermission !== null}
                            />
                            <Form.Text className="text-muted">
                                {editingPermission ? 'Code cannot be changed once created.' : 'Maximum 100 characters. Will be converted to uppercase. Must be unique.'}
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Description *</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={3}
                                name="description"
                                value={formData.description}
                                onChange={handleInputChange}
                                placeholder="Enter a clear description of what this permission allows"
                                required
                                maxLength={500}
                            />
                            <Form.Text className="text-muted">
                                Maximum 500 characters. Describe what access this permission grants.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Category *</Form.Label>
                            <Form.Select
                                name="category"
                                value={formData.category}
                                onChange={handleInputChange}
                                required
                            >
                                <option value="module">Module</option>
                                <option value="section">Section</option>
                                <option value="task">Task</option>
                            </Form.Select>
                            <Form.Text className="text-muted">
                                Select the category this permission belongs to.
                            </Form.Text>
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Check
                                type="checkbox"
                                name="is_module_level"
                                checked={formData.is_module_level}
                                onChange={handleInputChange}
                                label="Module Level Permission"
                            />
                            <Form.Text className="text-muted">
                                Check this if the permission applies at the module level rather than feature level.
                            </Form.Text>
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleCloseModal}>
                            Cancel
                        </Button>
                        <Button variant="primary" type="submit" disabled={saving}>
                            {saving ? (
                                <>
                                    <Spinner as="span" animation="border" size="sm" role="status" className="me-2" />
                                    Saving...
                                </>
                            ) : (
                                'Save Access Right'
                            )}
                        </Button>
                    </Modal.Footer>
                </Form>
            </Modal>

            {/* Connected Roles Modal */}
            <Modal show={showConnectedRolesModal} onHide={() => { setShowConnectedRolesModal(false); setConnectedRolesPermission(null); }} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <i className="bi bi-link-45deg me-2"></i>
                        Connected Roles
                        {connectedRolesPermission && (
                            <small className="text-muted ms-2">— {connectedRolesPermission.code}</small>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {loadingConnectedRoles ? (
                        <div className="text-center py-4">
                            <Spinner animation="border" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </Spinner>
                            <p className="mt-3 text-muted">Loading connected roles...</p>
                        </div>
                    ) : connectedRoles.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                            <i className="bi bi-people display-4"></i>
                            <div className="mt-2">No roles are currently assigned this permission.</div>
                        </div>
                    ) : (
                        <Table striped bordered hover responsive>
                            <thead className="table-dark">
                                <tr>
                                    <th>Role Name</th>
                                    <th>Role Code</th>
                                    <th>Created</th>
                                </tr>
                            </thead>
                            <tbody>
                                {connectedRoles.map((role) => (
                                    <tr key={role.id || role.enterprise_role_id}>
                                        <td className="fw-medium">{role.name || role.role_name || '-'}</td>
                                        <td><Badge bg="secondary">{role.code || role.role_code || '-'}</Badge></td>
                                        <td><small>{apiService.formatDate(role.created_at)}</small></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="text-muted small">
                            {connectedRoles.length} role{connectedRoles.length !== 1 ? 's' : ''} connected
                        </span>
                        <Button variant="secondary" onClick={() => { setShowConnectedRolesModal(false); setConnectedRolesPermission(null); }}>
                            Close
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default EnterpriseAccessRightsManagement;
